import type { FC } from 'react';
import { useMemo } from 'react';
import { Button, Flex, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, generatePath, createSearchParams } from 'react-router-dom';

import { useGetChannelListChannelListGetQuery } from '@shared/api';
import { authSelectors } from '@shared/redux/ducks/auth';
import { useAppSelector } from '@shared/redux';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { Table } from '@shared/ui/table';
import { Text } from '@shared/ui/antd';
import { useToggle } from '@shared/hooks/use-toggle';
import { Routes } from '@shared/constants/routes';
import { useGlobalConfirmModalActions } from '@shared/redux/ducks/global-confirm-modal';
import { useDeleteChannel } from '@modules/channels/hooks/use-delete-channel';
import { useActivateChannel } from '@modules/channels/hooks/use-activate-channel';
import { useHealthCheckChannel } from '@modules/channels/hooks/use-health-check-channel';
import { ChannelCreate } from '@modules/channels/ui/create-modal/create-modal';
import {
  toChannelCreateRouteType,
  type ChannelCreatableType,
} from '@modules/channels/lib/channel-type';
import { useGetIntegrations } from '@modules/integrations/hooks/use-get-integrations';
import { ChannelSettings } from '@shared/ui/channel-settings/channel-settings';
import { ChannelTypeIcon } from '@modules/channels/ui/channel-type-icon/channel-type-icon';

import styles from '../../prompt.module.scss';

interface IntegrationsSectionProps {
  agentId: number;
}

const IntegrationsCell: FC<{ channelId: number }> = ({ channelId }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'prompt.integrations.table' });
  const { integrations, isLoading, isError } = useGetIntegrations(channelId);

  if (isLoading) {
    return (
      <Table.TCellText variant="sm" textStyle={{ color: 'var(--color-text-tertiary)' }}>
        {t('loadingIntegrations')}
      </Table.TCellText>
    );
  }

  if (isError) {
    return (
      <Table.TCellText variant="sm" textStyle={{ color: 'var(--color-text-tertiary)' }}>
        {t('loadingError')}
      </Table.TCellText>
    );
  }

  if (!integrations?.length) {
    return (
      <Table.TCellText variant="sm" textStyle={{ color: 'var(--color-text-tertiary)' }}>
        {t('noIntegrations')}
      </Table.TCellText>
    );
  }

  const titles = integrations.map((integration) => integration.title || t('unnamedIntegration'));

  return <Table.TCellTextTooltip variant="sm" text={titles.join(', ')} maxLength={36} />;
};

export const IntegrationsSection: FC<IntegrationsSectionProps> = ({ agentId }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'prompt.integrations' });
  const { t: tChannels } = useTranslation('translation', { keyPrefix: 'channels' });
  const { Text: AntdText } = Typography;
  const navigate = useNavigate();
  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);

  const {
    state: isCreateChannelsModalOpen,
    setFalse: closeCreateChannelsModal,
    setTrue: openCreateChannelsModal,
  } = useToggle();

  const { openConfirmModal } = useGlobalConfirmModalActions();
  const { deleteChannel } = useDeleteChannel();
  const { updateChannel, isLoading: isToggleLoading, loadingChannelId } = useActivateChannel();

  const {
    healthCheckChannel,
    isLoading: isHealthCheckLoading,
    loadingChannelId: healthCheckId,
  } = useHealthCheckChannel();

  const { data, isLoading, isError, refetch } = useGetChannelListChannelListGetQuery(
    {
      organizationId: currentOrganization.id,
      agentId,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !currentOrganization?.id || !agentId,
    },
  );

  const rows = useMemo(() => data ?? [], [data]);

  const handleSelectChannelToUpdate = (channelId: number) => {
    navigate({
      pathname: generatePath(Routes.channels.update, {
        channelId,
      }),
      search: createSearchParams({ 'ai-agent-id': String(agentId) }).toString(),
    });
  };

  const handleSelectChannelType = (channelType: ChannelCreatableType) => {
    navigate({
      pathname: generatePath(Routes.channels.create, {
        type: toChannelCreateRouteType(channelType),
      }),
      search: createSearchParams({ 'ai-agent-id': String(agentId) }).toString(),
    });
  };

  const handleOpenConfirmModal = (id: number) => {
    openConfirmModal({
      title: tChannels('deleteModal.title'),
      text: (
        <Trans
          t={tChannels}
          i18nKey="deleteModal.text"
          values={{
            channel: rows.find((channel) => channel.id === id)?.name ?? '',
            interpolation: { escapeValue: false },
          }}
          components={{ bold: <AntdText strong /> }}
        />
      ),
      onConfirm: () => deleteChannel(id),
    });
  };

  return (
    <Flex vertical className={styles.element} gap={12}>
      <Flex gap={4} align="start" vertical>
        <Text variant="md" fontWeight={500}>
          {t('title')}
        </Text>

        <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('description')}
        </Text>
      </Flex>

      {isLoading && <LoadingState />}
      {!isLoading && isError && <FetchingErrorState refetch={refetch} />}

      {!isLoading && !isError && (
        <>
          <div className={styles.integrationsTable}>
            <Table maxHeight="400px">
              <Table.THead>
                <Table.THeadCellText width={166}>{t('table.type')}</Table.THeadCellText>
                <Table.THeadCellText width={166}>{t('table.address')}</Table.THeadCellText>
                <Table.THeadCellText width={166}>{t('table.name')}</Table.THeadCellText>
                <Table.THeadCellText width={166}>{t('table.integrations')}</Table.THeadCellText>
                <Table.THeadCellText width={120}>{t('table.status')}</Table.THeadCellText>
                <Table.THeadCellText
                  width={46}
                  style={{ textAlign: 'center' }}
                  textStyle={{ textAlign: 'center' }}
                >
                  <></>
                </Table.THeadCellText>
              </Table.THead>
              <Table.TBody>
                {rows.map((channel) => (
                  <Table.TRow key={channel.id}>
                    <Table.TCell>
                      <Flex align="center" gap={12}>
                        <ChannelTypeIcon type={channel.type} />
                        <Text
                          variant="sm"
                          fontWeight={400}
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {tChannels(`channelType.${channel.type}`)}
                        </Text>
                      </Flex>
                    </Table.TCell>
                    <Table.TCellTextTooltip
                      variant="sm"
                      text={channel.description || t('table.empty')}
                      maxLength={24}
                      textStyle={{ color: 'var(--color-text-tertiary)' }}
                    />
                    <Table.TCellTextTooltip
                      variant="sm"
                      text={channel.name || t('table.empty')}
                      maxLength={24}
                      textStyle={{ color: 'var(--color-text-primary)' }}
                    />
                    <IntegrationsCell channelId={channel.id} />
                    <Table.TCell>
                      <Text
                        variant="sm"
                        fontWeight={400}
                        className={channel.is_active ? styles.statusActive : styles.statusInactive}
                      >
                        {channel.is_active ? t('table.statusActive') : t('table.statusInactive')}
                      </Text>
                    </Table.TCell>
                    <Table.TCell key="actions" align="center" style={{ padding: 0 }}>
                      <Flex align="center" justify="center">
                        <ChannelSettings
                          channelId={channel.id}
                          isActive={channel.is_active}
                          isToggleLoading={isToggleLoading && loadingChannelId === channel.id}
                          isHealthCheckLoading={
                            isHealthCheckLoading && healthCheckId === channel.id
                          }
                          onToggleActive={(status) => updateChannel(channel.id, status)}
                          onHealthCheck={() => healthCheckChannel(channel.id)}
                          onSelectChannelToUpdate={handleSelectChannelToUpdate}
                          onDeleteChannel={handleOpenConfirmModal}
                        />
                      </Flex>
                    </Table.TCell>
                  </Table.TRow>
                ))}
              </Table.TBody>
            </Table>
          </div>
          <Flex>
            <Button type="default" size="large" onClick={openCreateChannelsModal}>
              {t('addButton')}
            </Button>
          </Flex>
        </>
      )}

      <ChannelCreate
        isOpen={isCreateChannelsModalOpen}
        onClose={closeCreateChannelsModal}
        onSelectType={handleSelectChannelType}
      />
    </Flex>
  );
};
